import { ListTemplate, ListItem, TemplateCategory } from '../types/lists';

const LIST_TEMPLATES_STORAGE_KEY = 'godmode_list_templates';

export class TemplatesService {
  private templates: ListTemplate[] = [];

  constructor() {
    this.loadTemplates();
  }

  // Load templates from localStorage
  private loadTemplates(): void {
    try {
      const storedTemplates = localStorage.getItem(LIST_TEMPLATES_STORAGE_KEY);
      if (storedTemplates) {
        this.templates = JSON.parse(storedTemplates).map((template: any) => ({
          ...template,
          createdAt: new Date(template.createdAt),
          updatedAt: new Date(template.updatedAt)
        }));
      } else {
        this.templates = this.getDefaultTemplates();
        this.saveTemplates();
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      this.templates = this.getDefaultTemplates();
    }
  }

  // Save templates to localStorage
  private saveTemplates(): void {
    try {
      localStorage.setItem(LIST_TEMPLATES_STORAGE_KEY, JSON.stringify(this.templates));
    } catch (error) {
      console.error('Error saving templates:', error);
    }
  }

  // Get all templates
  getTemplates(): ListTemplate[] {
    return [...this.templates];
  }

  // Get template by ID
  getTemplateById(id: string): ListTemplate | undefined {
    return this.templates.find(t => t.id === id);
  }

  // Get templates by category
  getTemplatesByCategory(category: TemplateCategory): ListTemplate[] {
    return this.templates.filter(t => t.category === category);
  }

  // Create a new template
  async createTemplate(data: {
    title: string;
    description: string;
    category: string;
    color?: string;
    estimatedHours?: number;
  }): Promise<ListTemplate> {
    const newTemplate: ListTemplate = {
      id: this.generateId('template'),
      title: data.title,
      description: data.description,
      category: data.category,
      items: [],
      usageCount: 0,
      color: data.color,
      estimatedHours: data.estimatedHours,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.templates.push(newTemplate);
    this.saveTemplates();
    console.log('✅ Template created successfully:', newTemplate.title);
    return newTemplate;
  }

  // Update an existing template
  async updateTemplate(templateId: string, updates: Partial<ListTemplate>): Promise<ListTemplate> {
    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Template not found');
    }

    const updatedTemplate = {
      ...this.templates[templateIndex],
      ...updates,
      updatedAt: new Date(),
    };

    this.templates[templateIndex] = updatedTemplate;
    this.saveTemplates();
    console.log('✅ Template updated successfully:', updatedTemplate.title);
    return updatedTemplate;
  }

  // Delete a template
  async deleteTemplate(templateId: string): Promise<void> {
    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Template not found');
    }

    this.templates.splice(templateIndex, 1);
    this.saveTemplates();
    console.log('✅ Template deleted successfully');
  }

  // Increment usage count when template is cloned
  incrementUsageCount(templateId: string): void {
    const template = this.templates.find(t => t.id === templateId);
    if (template) {
      template.usageCount += 1;
      template.updatedAt = new Date();
      this.saveTemplates();
    }
  }

  // Generate unique ID
  private generateId(prefix: string = 'template'): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  // Add item to template
  async addTemplateItem(templateId: string, content: string, itemData?: Partial<Omit<ListItem, 'id' | 'createdAt' | 'priority'>>): Promise<ListTemplate> {
    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Template not found');
    }

    const template = this.templates[templateIndex];
    const newItem = {
      content,
      updatedAt: new Date(),
      ...itemData,
    };

    template.items.push(newItem);
    template.updatedAt = new Date();
    this.saveTemplates();
    console.log('✅ Template item added successfully:', content);

    return template;
  }

  // Update template item
  async updateTemplateItem(templateId: string, itemIndex: number, updates: Partial<Omit<ListItem, 'id' | 'createdAt' | 'priority'>>): Promise<ListTemplate> {
    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Template not found');
    }

    const template = this.templates[templateIndex];
    if (itemIndex < 0 || itemIndex >= template.items.length) {
      throw new Error('Item not found');
    }

    template.items[itemIndex] = {
      ...template.items[itemIndex],
      ...updates,
      updatedAt: new Date(),
    };
    template.updatedAt = new Date();
    this.saveTemplates();
    console.log('✅ Template item updated successfully');

    return template;
  }

  // Delete template item
  async deleteTemplateItem(templateId: string, itemIndex: number): Promise<ListTemplate> {
    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Template not found');
    }

    const template = this.templates[templateIndex];
    if (itemIndex < 0 || itemIndex >= template.items.length) {
      throw new Error('Item not found');
    }

    template.items.splice(itemIndex, 1);
    template.updatedAt = new Date();
    this.saveTemplates();
    console.log('✅ Template item deleted successfully');

    return template;
  }

  // Reorder template items
  async reorderTemplateItems(templateId: string, reorderedItems: Array<Omit<ListItem, 'id' | 'createdAt' | 'priority'>>): Promise<ListTemplate> {
    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Template not found');
    }

    const template = this.templates[templateIndex];
    template.items = reorderedItems;
    template.updatedAt = new Date();
    this.saveTemplates();
    console.log('✅ Template items reordered successfully');

    return template;
  }

  // Get default templates
  private getDefaultTemplates(): ListTemplate[] {
    return [
      {
        id: 'template_morning_routine',
        title: 'Morning Routine',
        description: 'Start your day with purpose and energy',
        category: 'Daily Routines',
        usageCount: 0,
        color: '#FF6B6B',
        estimatedHours: 2,
        items: [
          { content: 'Wake up at consistent time', updatedAt: new Date('2024-01-01') },
          { content: 'Drink glass of water', updatedAt: new Date('2024-01-01') },
          { content: '10 minutes meditation or breathing', updatedAt: new Date('2024-01-01') },
          { content: 'Review daily priorities', updatedAt: new Date('2024-01-01') },
          { content: 'Exercise or stretching (20 mins)', updatedAt: new Date('2024-01-01') },
          { content: 'Healthy breakfast', updatedAt: new Date('2024-01-01') },
          { content: 'Review calendar and tasks', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
      {
        id: 'template_project_kickoff',
        title: 'Project Kickoff',
        description: 'Essential steps to start any new project successfully',
        category: 'Work Projects',
        usageCount: 0,
        color: '#4ECDC4',
        estimatedHours: 8,
        items: [
          { content: 'Define project scope and objectives', updatedAt: new Date('2024-01-01') },
          { content: 'Identify key stakeholders', updatedAt: new Date('2024-01-01') },
          { content: 'Create project timeline and milestones', updatedAt: new Date('2024-01-01') },
          { content: 'Set up project workspace/tools', updatedAt: new Date('2024-01-01') },
          { content: 'Schedule kickoff meeting', updatedAt: new Date('2024-01-01') },
          { content: 'Create communication plan', updatedAt: new Date('2024-01-01') },
          { content: 'Identify risks and mitigation strategies', updatedAt: new Date('2024-01-01') },
          { content: 'Define success criteria', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
      {
        id: 'template_weekly_review',
        title: 'Weekly Review',
        description: 'Reflect and plan for continuous improvement',
        category: 'Daily Routines',
        usageCount: 0,
        color: '#45B7D1',
        estimatedHours: 1,
        items: [
          { content: 'Review completed tasks from the week', updatedAt: new Date('2024-01-01') },
          { content: 'Assess progress on goals', updatedAt: new Date('2024-01-01') },
          { content: 'Identify wins and challenges', updatedAt: new Date('2024-01-01') },
          { content: 'Plan priorities for next week', updatedAt: new Date('2024-01-01') },
          { content: 'Schedule important meetings/tasks', updatedAt: new Date('2024-01-01') },
          { content: 'Update project statuses', updatedAt: new Date('2024-01-01') },
          { content: 'Clean up workspace and digital files', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
      {
        id: 'template_learning_new_skill',
        title: 'Learning New Skill',
        description: 'Structured approach to master any new skill',
        category: 'Learning & Education',
        usageCount: 0,
        color: '#F7DC6F',
        estimatedHours: 40,
        items: [
          { content: 'Research and choose learning resources', updatedAt: new Date('2024-01-01') },
          { content: 'Set specific learning goals', updatedAt: new Date('2024-01-01') },
          { content: 'Create study schedule', updatedAt: new Date('2024-01-01') },
          { content: 'Join relevant communities/forums', updatedAt: new Date('2024-01-01') },
          { content: 'Practice daily (minimum 30 mins)', updatedAt: new Date('2024-01-01') },
          { content: 'Find a mentor or study buddy', updatedAt: new Date('2024-01-01') },
          { content: 'Apply knowledge to real projects', updatedAt: new Date('2024-01-01') },
          { content: 'Track progress and adjust plan', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
      {
        id: 'template_home_organization',
        title: 'Home Organization',
        description: 'Declutter and organize your living space',
        category: 'Home & Personal',
        usageCount: 0,
        color: '#BB8FCE',
        estimatedHours: 12,
        items: [
          { content: 'Choose one room/area to focus on', updatedAt: new Date('2024-01-01') },
          { content: 'Remove everything from the space', updatedAt: new Date('2024-01-01') },
          { content: 'Sort items: keep, donate, trash', updatedAt: new Date('2024-01-01') },
          { content: 'Clean the empty space thoroughly', updatedAt: new Date('2024-01-01') },
          { content: 'Organize storage solutions', updatedAt: new Date('2024-01-01') },
          { content: 'Put back only kept items logically', updatedAt: new Date('2024-01-01') },
          { content: 'Label storage containers/areas', updatedAt: new Date('2024-01-01') },
          { content: 'Donate or dispose of unwanted items', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
      {
        id: 'template_fitness_routine',
        title: 'Fitness Routine Setup',
        description: 'Build a sustainable exercise habit',
        category: 'Health & Fitness',
        usageCount: 0,
        color: '#58D68D',
        estimatedHours: 2,
        items: [
          { content: 'Assess current fitness level', updatedAt: new Date('2024-01-01') },
          { content: 'Set realistic fitness goals', updatedAt: new Date('2024-01-01') },
          { content: 'Choose enjoyable types of exercise', updatedAt: new Date('2024-01-01') },
          { content: 'Schedule workout times in calendar', updatedAt: new Date('2024-01-01') },
          { content: 'Prepare workout space/equipment', updatedAt: new Date('2024-01-01') },
          { content: 'Plan workout playlist/entertainment', updatedAt: new Date('2024-01-01') },
          { content: 'Track progress with app or journal', updatedAt: new Date('2024-01-01') },
          { content: 'Find accountability partner', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
      {
        id: 'template_travel_planning',
        title: 'Travel Planning',
        description: 'Organize an amazing trip from start to finish',
        category: 'Travel & Events',
        usageCount: 0,
        color: '#F8C471',
        estimatedHours: 6,
        items: [
          { content: 'Set travel dates and budget', updatedAt: new Date('2024-01-01') },
          { content: 'Research destinations and activities', updatedAt: new Date('2024-01-01') },
          { content: 'Book flights and accommodation', updatedAt: new Date('2024-01-01') },
          { content: 'Plan daily itinerary', updatedAt: new Date('2024-01-01') },
          { content: 'Check passport/visa requirements', updatedAt: new Date('2024-01-01') },
          { content: 'Research local customs and phrases', updatedAt: new Date('2024-01-01') },
          { content: 'Pack appropriate clothing/items', updatedAt: new Date('2024-01-01') },
          { content: 'Arrange pet/house sitting if needed', updatedAt: new Date('2024-01-01') },
          { content: 'Notify bank and set up international plan', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
      {
        id: 'template_financial_checkup',
        title: 'Monthly Financial Checkup',
        description: 'Review and optimize your financial health',
        category: 'Financial Planning',
        usageCount: 0,
        color: '#82E0AA',
        estimatedHours: 3,
        items: [
          { content: 'Review bank and credit card statements', updatedAt: new Date('2024-01-01') },
          { content: 'Update budget with actual spending', updatedAt: new Date('2024-01-01') },
          { content: 'Check progress on savings goals', updatedAt: new Date('2024-01-01') },
          { content: 'Review investment portfolio performance', updatedAt: new Date('2024-01-01') },
          { content: 'Pay all bills and schedule upcoming ones', updatedAt: new Date('2024-01-01') },
          { content: 'Look for ways to reduce expenses', updatedAt: new Date('2024-01-01') },
          { content: 'Update financial goals if needed', updatedAt: new Date('2024-01-01') },
          { content: 'Set financial priorities for next month', updatedAt: new Date('2024-01-01') },
        ],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
      },
    ];
  }
}

// Export singleton instance
export const templatesService = new TemplatesService();
